import { isBuildCRM } from './EnvironmentUtils';
import { saveSessionObject } from './SessionStorageUtils';

/**
 * Update the browser history stack so that the back and forward button will utilise the form. This will save the step into the URL so that it can pulled later to when the back and forward buttons are used.
 *
 * @param {number} currentPage - The current page number or the step number of the form.
 * @param {String} formName - Current form name which should match the title in meta data of the form pages.
 * @param {boolean} replaceHistory - Instead of pushing the state, replace the history.
 * @returns {String} - The updated query part of the url, for writing unit testing purpose only
 */
export const updateBrowserHistory = (currentPage, formName, replaceHistory = false) => {
  // Grab current the query string portion of the URL.
  const queryString = window.location.search;

  // Get parameters of current URL
  const tempArray = queryString.split('?');
  let additionalURL = tempArray[1];

  // Change change step number to match current page
  if (additionalURL) {
    const params = additionalURL.split('&');
    for (let i = 0; i < params.length; i++) {
      if (params[i].split('=')[0] === 'step') {
        params[i] = `step=${currentPage}`;
      }
    }
    additionalURL = `?${params.join('&')}`;
  } else {
    additionalURL = `?step=${currentPage}`;
  }

  // Only push the state if not running in CRM mode.
  if (!isBuildCRM() && !replaceHistory) {
    window.history.pushState({ currentPage }, formName, additionalURL);
  } else if (replaceHistory) {
    window.history.replaceState({ currentPage }, formName, additionalURL);
  }

  // Running Jest tests won't be able to pick up the changes to window.location when it's modified via the Vue components. So here add a returned value only for unit testing purpose to help Jest recognize the changes.
  return additionalURL;
};

/**
 * This will extract the list of arguments to be used with the assumption that index 3 and index 4 is the page
 * number that is to overwrite the calculated page, and whether the browser history is to be updated.
 *
 * @param {Array} args The list of arguments that have been passed through.
 * @returns {Object} The arguments in an accessible object.
 */
const fetchPageArugments = (args) => {
  const returnObject = { updateBrowserHistory: true };
  if (args.length >= 1) {
    returnObject.overwritePageNumber = args[0];
  }

  if (args.length >= 2) {
    returnObject.updateBrowserHistory = args[1];
  }

  return returnObject;
};

/**
 * This function will handle the next button in multi-step form and navigate to next form step.
 *
 * @param {number} currentPage - The current page number or the step number of the form.
 * @param {String} storageKey - This will be the session storage key which is defined in SessionStorageUtils.js to save the current page into session.
 * @param {String} formName - Current form name which should match the title in meta data of the form pages.
 * @returns {number} - Updated page number after button is pressed.
 */
export const getNextPage = (currentPage, storageKey, formName, ...args) => {
  let pageNumber = currentPage + 1;
  const argObject = fetchPageArugments(args);

  if (argObject.overwritePageNumber !== undefined) {
    pageNumber = argObject.overwritePageNumber;
  }

  // Save the current page into the storage
  saveSessionObject(pageNumber, storageKey);

  if (argObject.updateBrowserHistory) {
    updateBrowserHistory(pageNumber, formName);
  }

  return pageNumber;
};

/**
 * This function will handle the Back button in multi-step form and navigate to previous form step.
 *
 * @param {number} currentPage - The current page number or the step number of the form.
 * @param {String} storageKey - This will be the session storage key which is defined in SessionStorageUtils.js to save the current page into session.
 * @param {String} formName - Current form name which should match the title in meta data of the form pages.
 * @returns {number} - Updated page number after button is pressed.
 */
export const getPreviousPage = (currentPage, storageKey, formName, ...args) => {
  let pageNumber = currentPage - 1;

  const argObject = fetchPageArugments(args);

  if (argObject.overwritePageNumber !== undefined) {
    pageNumber = argObject.overwritePageNumber;
  }

  // Save the current page into the storage
  saveSessionObject(pageNumber, storageKey);

  if (argObject.updateBrowserHistory) {
    updateBrowserHistory(pageNumber, formName);
  }

  return pageNumber;
};

/**
 * Handles the multi-step form navigation links being clicked.
 *
 * @param {number} pageNumber - The step number that the page should update to.
 * @param {boolean} ifUpdateBrowserHistory - Defaults to true, but it may not be required if the update of current page is responding to a state change in the browser history.
 * @param {String} formName - Current form name which should match the title in meta data of the form pages.
 * @param {String} storageKey - This will be the session storage key which is defined in SessionStorageUtils.js to save the current page into session.
 * @returns {number} - Updated page number after button is pressed.
 */
export const updateCurrentPage = (pageNumber, ifUpdateBrowserHistory = true, formName, storageKey) => {
  const currentPage = pageNumber;
  // Save the current page into the storage
  saveSessionObject(pageNumber, storageKey);

  // Update the history state if required.
  if (ifUpdateBrowserHistory) {
    updateBrowserHistory(pageNumber, formName);
  }

  return currentPage;
};

/**
 * Replaces the URL path to have the base path so that query parameter is cleared from the URL bar.
 *
 * @param {String} route - the route of the current step, should be this.$route when using this function.
 */
export const replaceToBaseUrlPath = (route) => {
  // Check if there is a step query in the URL when loading the data
  if (route.query && route.query.step && !isBuildCRM()) {
    window.history.replaceState(null, null, route.path);

    // Return for unit testing purpose only, because Jest can't recognize window history changes via Vue components.
    return true;
  }
  // Return for unit testing purpose only, because Jest can't recognize window history changes via Vue components.
  return false;
};

/**
 * Replaces the link provided from Kentico to remove the ~ and swaps token.
 *
 * @param {String} link
 * @returns {String} - Updated link
 */
export const formatKenticoLink = (link) => {
  return link.replace(/~/i, '').tokenSwap();
};
